import { useMemo } from 'react';
import { createFileRoute, Link, Outlet } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxcRegistrationV1AccountQueryServiceAccountGet } from '@interxfi/api-client/generated/hooks/ixc-registration-v1-account-query-service-account-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { ButtonProps } from '@interxfi/design-system/components/button/button';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DropdownMenu } from '@interxfi/design-system/components/dropdown-menu/dropdown-menu';
import { baseTextLinkTypographyBySize, TextLink } from '@interxfi/design-system/components/link/text-link/text-link';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Typography } from '@interxfi/design-system/components/typography';
import BankIcon from '@interxfi/design-system/icons/react/icon-bank';
import BillIcon from '@interxfi/design-system/icons/react/icon-bill';
import BulletsIcon from '@interxfi/design-system/icons/react/icon-bullets';
import DocIcon from '@interxfi/design-system/icons/react/icon-doc';
import FreezeIcon from '@interxfi/design-system/icons/react/icon-freeze';
import OrdersIcon from '@interxfi/design-system/icons/react/icon-orders';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import ShieldIcon from '@interxfi/design-system/icons/react/icon-shield';
import ToggleIcon from '@interxfi/design-system/icons/react/icon-toggle';
import TransferIcon from '@interxfi/design-system/icons/react/icon-transfer';
import { useBreakpoint } from '@interxfi/design-system/util/responsive';
import {
  type IxcRegistrationV1AccountState,
  IxcRegistrationV1AccountStatus,
  IxcRegistrationV1ControlsMarginType,
  IxcRegistrationV1Purpose,
} from '@interxfi/types/generated/api';
import { AllInOneOrderTicketDrawer } from '../../../../../../../features/all-in-one-ticket/all-in-one-order-ticket-drawer';
import { BillingChargeCreateDrawer } from '../../../../../../../features/billing/charge/billing-charge-create-drawer';
import {
  useCanAccountCreateFundingTransfer,
  useCanProgramCreditCustomerAccounts,
  useCanProgramDebitCustomerAccounts,
} from '../../../../../../../features/funding/funding-permission-helpers';
import { FundingSourceDetailDrawer } from '../../../../../../../features/funding/source/detail-drawer/funding-source-detail-drawer';
import { CreateFundingTransferDrawer } from '../../../../../../../features/funding/transfer/create-funding-transfer-drawer';
import { useCanAccountCreateOrder } from '../../../../../../../features/order/order-permission-helpers';
import { AccountBreadcrumb } from '../../../../../../../features/registration/account/account-breadcrumb';
import { AccountFreezeConfirmDialog } from '../../../../../../../features/registration/account/account-freeze-confirm-alert-dialog';
import { AccountMarginTypePill } from '../../../../../../../features/registration/account/account-margin-type-pill';
import { AccountRegistrationOrPurposePill } from '../../../../../../../features/registration/account/account-registration-or-purpose-pill';
import { useAccountPrimaryAddress } from '../../../../../../../features/registration/account/account-registration-utils';
import { AccountStatusPill } from '../../../../../../../features/registration/account/account-status-pill';
import { AccountUnrestrictConfirmDialog } from '../../../../../../../features/registration/account/account-unrestrict-confirm-alert-dialog';
import { AccountCheckWorkflowDrawer } from '../../../../../../../features/registration/account/checks/account-check-workflow-drawer/account-check-workflow-drawer';
import { useIsAccountFrozen, useIsAccountRestricted } from '../../../../../../../features/registration/account/freeze-utils';
import { AccountPortfolioValueBar } from '../../../../../../../features/registration/account/portfolio-value/account-portfolio-value-bar';
import { RegistrationAccountEventDrawer } from '../../../../../../../features/registration/account/registration-account-event';
import { RegistrationAccountPageHeader } from '../../../../../../../features/registration/account/registration-account-page-header';
import { getRegistrationAccountDetailPath } from '../../../../../../../features/registration/account/registration-account-routing';
import { RegistrationAccountStatusBar } from '../../../../../../../features/registration/account/registration-account-status-bar';
import { getSortedProfileSummaries } from '../../../../../../../features/registration/account/utils';
import { getNameFromProfileSummaryType } from '../../../../../../../features/registration/profile/profile-utils';
import { getRegistrationProfileDetailPath } from '../../../../../../../features/registration/profile/registration-profile-routing';
import { formatAddress, ONE_LINE_ADDRESS_FORMAT_LAYOUTS } from '../../../../../../../features/registration/shared-utils/address';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_summary-list')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-summary-asset`,
        label: <AccountBreadcrumb accountId={params.accountId} />,
        to: `${getRegistrationAccountDetailPath(params.accountId)}/asset`,
        suffix: <CopyButton content={params.accountId} label="Copy Account ID" tooltip={{ copy: params.accountId, copied: 'Copied Account ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Account'),
  component: AccountSummaryListLayout,
});

const createMenuActivatorProps = {
  affixIcon: <PlusIcon aria-hidden />,
  size: 'small',
  variant: 'primary',
} as const satisfies ButtonProps;

function useCreateMenuItems(accountId: string) {
  const canCreateOrder = useCanAccountCreateOrder(accountId);
  const canCreateFundingTransfer = useCanAccountCreateFundingTransfer(accountId);
  const { data: accountData } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const canDebitAccounts = useCanProgramDebitCustomerAccounts(accountData?.account?.programId);
  const canCreditAccounts = useCanProgramCreditCustomerAccounts(accountData?.account?.programId);
  const isCustomerAccount = accountData?.account?.data?.purpose === IxcRegistrationV1Purpose.Customer;
  const shouldShowChargeItem =
    (canDebitAccounts || canCreditAccounts) && (accountData?.account?.data?.purpose === IxcRegistrationV1Purpose.Billing || isCustomerAccount);

  const items = useMemo(
    () =>
      [
        ...(canCreateOrder
          ? [
              {
                id: 'createOrder',
                label: 'Place an Order',
                icon: <OrdersIcon aria-hidden />,
                interaction: {
                  type: 'drawer',
                  drawer: { Component: AllInOneOrderTicketDrawer, props: { accountId } },
                },
                requiresWrite: true,
              } satisfies EntityActionMenuItem,
            ]
          : []),
        ...(canCreateFundingTransfer
          ? [
              {
                id: 'createTransfer',
                label: 'Make a Transfer',
                icon: <TransferIcon aria-hidden />,
                interaction: { type: 'drawer', drawer: { Component: CreateFundingTransferDrawer, props: { accountId } } },
                requiresWrite: true,
              } satisfies EntityActionMenuItem,
            ]
          : []),
        ...(shouldShowChargeItem
          ? [
              {
                id: 'createBillingCharge',
                label: 'Charge a Fee',
                icon: <BillIcon aria-hidden />,
                interaction: {
                  type: 'drawer',
                  drawer: { Component: BillingChargeCreateDrawer, props: { accountId: isCustomerAccount ? accountId : undefined } },
                },
                requiresWrite: true,
              } satisfies EntityActionMenuItem,
            ]
          : []),
      ] satisfies EntityActionMenuItem[],
    [canCreateOrder, canCreateFundingTransfer, accountId, shouldShowChargeItem, isCustomerAccount],
  );

  return items;
}

function getEntityMenuItems(accountId: string, accountState: IxcRegistrationV1AccountState | undefined, isFrozen: boolean, isRestricted: boolean) {
  const accountPathBase = getRegistrationAccountDetailPath(accountId);

  return {
    eventDrawer: {
      type: 'drawer',
      drawer: { Component: RegistrationAccountEventDrawer, props: { request: { accountId } } },
    },
    actions: [
      {
        id: 'accountDetails',
        label: 'Account Details',
        icon: <BulletsIcon aria-hidden />,
        to: `${accountPathBase}/detail`,
        LinkElement: Link,
      },
      {
        id: 'documents',
        label: 'Documents',
        icon: <DocIcon aria-hidden />,
        to: `${accountPathBase}/document`,
        LinkElement: Link,
      },
      {
        id: 'fundingSources',
        label: 'Funding Sources',
        icon: <BankIcon aria-hidden />,
        interaction: { type: 'drawer', drawer: { Component: FundingSourceDetailDrawer, props: { accountId } } },
      },
      ...(accountState?.data?.spec && 'customer' in accountState.data.spec
        ? [
            {
              id: 'checks',
              label: 'Review Checks',
              icon: <ShieldIcon aria-hidden />,
              interaction: { type: 'drawer', drawer: { Component: AccountCheckWorkflowDrawer, props: { accountId } } },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(accountState?.status === IxcRegistrationV1AccountStatus.Open && !isFrozen
        ? [
            {
              id: 'freeze',
              label: 'Freeze Account',
              icon: <FreezeIcon aria-hidden />,
              interaction: { type: 'modal', modal: { Component: AccountFreezeConfirmDialog, props: { accountId } } },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(accountState?.status === IxcRegistrationV1AccountStatus.Open && !isFrozen && isRestricted
        ? [
            {
              id: 'unrestrict',
              label: 'Unrestrict Account',
              icon: <ToggleIcon aria-hidden />,
              interaction: { type: 'modal', modal: { Component: AccountUnrestrictConfirmDialog, props: { accountId, variant: 'unrestrict' } } },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      // TODO: uncomment when close account functionality is implemented
      // ...(accountState?.status === IxcRegistrationV1AccountStatus.Open
      //   ? [
      //       {
      //         id: 'closeAccount',
      //         label: 'Close Account',
      //         tone: 'danger-dk',
      //         icon: <XLargeIcon aria-hidden />,
      //         interaction: { type: 'modal', modal: { Component: ConfirmCloseAccountDialog, props: { accountId } } },
      //         requiresWrite: true,
      //       } as const satisfies EntityActionMenuItem,
      //     ]
      //   : []),
    ],
  } as const satisfies EntityActionMenuConfig;
}

function AccountSummaryListLayout() {
  const { accountId } = Route.useParams();
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const { data, isPending, error } = useIxcRegistrationV1AccountQueryServiceAccountGet({ accountId });
  const [frozen, frozenSrcA, frozenSrcB] = useIsAccountFrozen(accountId);
  const [restricted, restrictedSrcA, restrictedSrcB] = useIsAccountRestricted(accountId);
  const isActionablyFrozen =
    frozen && ((frozenSrcA === 'clearing' && activeTenantIsClearing) || frozenSrcA === 'program' || frozenSrcB === 'program');
  const isActionablyRestricted =
    restricted && ((restrictedSrcA === 'clearing' && activeTenantIsClearing) || restrictedSrcA === 'program' || restrictedSrcB === 'program');
  const showPending = Boolean(isPending || error);
  const isMobile = useBreakpoint('sm');
  const createMenuItems = useCreateMenuItems(accountId);
  const entityMenuItems = useMemo(
    () => getEntityMenuItems(accountId, data?.account, isActionablyFrozen, isActionablyRestricted),
    [accountId, data?.account, isActionablyFrozen, isActionablyRestricted],
  );
  const sortedAccountHolders = useMemo(() => getSortedProfileSummaries(data?.account), [data?.account]);
  const primaryAddress = useAccountPrimaryAddress(accountId);

  const AccountHolderInfoContainer = isMobile ? VerticalStack : HorizontalStack;
  const accountHolderInfoContainerProps = isMobile
    ? ({
        gap: 4,
      } as const)
    : ({
        gap: 4,
        wrap: 'nowrap',
        align: 'space-between',
        blockAlign: 'center',
      } as const);

  return (
    <PageLayout gap={7}>
      <PageLayout.Section noBorder>
        <VerticalStack gap={7}>
          <RegistrationAccountStatusBar accountId={accountId} />

          <RegistrationAccountPageHeader
            accountId={accountId}
            actions={
              <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
                {createMenuItems.length ? (
                  <DropdownMenu
                    activatorProps={createMenuActivatorProps}
                    align="end"
                    items={createMenuItems}
                    label="Account actions"
                    labelIsHidden
                    matchActivatorWidth={false}
                    side="bottom"
                  />
                ) : null}

                <EntityActionMenu {...entityMenuItems} isPending={showPending} />
              </HorizontalStack>
            }
            pills={
              <>
                <AccountStatusPill isPending={showPending} size="large" status={data?.account?.status} />
                <AccountRegistrationOrPurposePill account={data?.account} isPending={showPending} size="large" />
                <AccountMarginTypePill
                  isPending={showPending}
                  marginType={data?.account?.data?.controls?.registration?.settings?.marginType || IxcRegistrationV1ControlsMarginType.Cash}
                  size="large"
                />
              </>
            }
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <AccountHolderInfoContainer {...accountHolderInfoContainerProps}>
          <AccountPortfolioValueBar accountId={accountId} />

          <VerticalStack gap={2}>
            <HorizontalStack align={isMobile ? 'start' : 'end'} gap={1}>
              {showPending ? (
                <Skeleton charCount={20} type="line" />
              ) : (
                sortedAccountHolders.map((holder, i, arr) => {
                  const name = getNameFromProfileSummaryType(holder.data);
                  const linkTo = getRegistrationProfileDetailPath(holder.profileId);

                  return (
                    <Typography key={holder.profileId} {...baseTextLinkTypographyBySize.xsmall}>
                      {linkTo ? (
                        <TextLink LinkElement={Link} size="xsmall" to={linkTo} underline="hover">
                          {name}
                        </TextLink>
                      ) : (
                        name
                      )}
                      {i < arr.length - 1 ? ', ' : null}
                    </Typography>
                  );
                })
              )}
            </HorizontalStack>

            <HorizontalStack align={isMobile ? 'start' : 'end'} gap={1}>
              {!primaryAddress ? (
                <Skeleton charCount={25} type="line" />
              ) : (
                <TextLink LinkElement={Link} size="xsmall" to={`/registration/account/${accountId}/detail`} underline="hover">
                  {formatAddress(primaryAddress, ONE_LINE_ADDRESS_FORMAT_LAYOUTS)}
                </TextLink>
              )}
            </HorizontalStack>
          </VerticalStack>
        </AccountHolderInfoContainer>
      </PageLayout.Section>

      <Outlet />
    </PageLayout>
  );
}
